import React, { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  AccountBalance,
  Person,
  BackupTable,
  StickyNote2,
  Groups,
  AccessTime,
  CalendarMonth,
  EventSeat,
  Mail,
  MarkunreadMailbox,
  Info,
  Folder,
  Logout,
  ManageAccounts,
  Redeem,
  Receipt,
  BorderColor,
  Settings,
  AltRoute,
  Lock,
  PlaylistAddCheck,
  StarBorder,
  MenuBook,
} from '@mui/icons-material';
import { AvatarPicture } from './AvatarPicture';
import { SupportDialog } from './SupportDialog';
import { IConfig } from '../Interfaces/IConfig';
import { Notifications } from './Notifications';
import { TimeRecordDialog } from '../TimeRecord/TimeRecordDialog';
import { useTheme } from '@mui/material';
import { ChangelogMenu } from '../Changelog/ChangelogMenu';
import { OwnTasks } from '../Task/OwnTasks';
import { useGetConfig } from '../hooks/reactQuerys/ConfigQueries';
import { customFetcher } from '../hooks/useFetch';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

type IProps = {
  children?: React.ReactNode;
  configObject: IConfig;
};

export const AppShell = ({ children, configObject }: IProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showTimeRecordDialog, setShowTimeRecordDialog] = useState(false);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const [anchorElChangelog, setAnchorElChangelog] =
    React.useState<null | HTMLElement>(null);

  // App/AppSettings werden vom LicenseServer geliefert (config.App). Der
  // Kalender-Menüpunkt erscheint nur, wenn 'calendar' Teil der lizenzierten
  // App-Liste ist.
  const { config: appConfig } = useGetConfig();
  const isCalendarEnabled = (appConfig?.Module ?? []).includes('calendar');

  const isMenuOpen = Boolean(anchorElUser);
  const isChangelogMenuOpen = Boolean(anchorElChangelog);

  const handleLogout = async () => {
    // WICHTIG: Backend-Logout (DELETE /oauth) muss VOR dem Token-Remove
    // laufen, damit der Authorization-Header noch gesetzt ist und das
    // Backend die richtige UserSession-Zeile loeschen kann. Sonst bleibt
    // die alte Session in der DB und erscheint beim Re-Login als zweite
    // aktive Session.
    try {
      await customFetcher({ endpoint: '/oauth', method: 'DELETE' });
    } catch {
      // Idempotent: auch wenn der Backend-Call scheitert (Token bereits
      // abgelaufen, Netzwerk weg), trotzdem lokal abmelden.
    }
    localStorage.removeItem('SessionToken');
    localStorage.removeItem('scope');
    localStorage.removeItem('role');
    window.location.reload();
  };

  const scopes = localStorage.getItem('scope')?.split(',') ?? [];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position='fixed' open={isSidebarOpen}>
        <Toolbar>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                size='large'
                edge='start'
                color='inherit'
                aria-label='menu'
                sx={{
                  marginRight: 5,
                  ...(isSidebarOpen && { display: 'none' }),
                }}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant='h6'
                noWrap
                component='div'
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                title='Zur Startseite'
              >
                DiNo | {configObject.company_name} | &copy; LeXtorByte
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setAnchorElChangelog(event.currentTarget)
                }
              >
                <Redeem sx={{ color: 'white' }} />
              </IconButton>

              <OwnTasks />
              <Notifications />

              <ChangelogMenu
                open={isChangelogMenuOpen}
                onClose={() => setAnchorElChangelog(null)}
                anchorEl={anchorElChangelog}
              />

              <Tooltip title='Einstellungen öffnen'>
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    setAnchorElUser(event.currentTarget)
                  }
                >
                  <AvatarPicture />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorElUser}
                open={isMenuOpen}
                onClose={() => setAnchorElUser(null)}
                sx={{
                  '& .MuiList-padding': {
                    paddingTop: 0,
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.grey[500],
                    p: 1,
                  }}
                >
                  {configObject.version}
                </Typography>
                <Divider sx={{ m: 0 }} />
                <SupportDialog
                  open={showSupportDialog}
                  onClose={() => setShowSupportDialog(false)}
                />
                <MenuItem onClick={() => setShowSupportDialog(true)}>
                  Support
                </MenuItem>

                <TimeRecordDialog
                  open={showTimeRecordDialog}
                  onClose={() => setShowTimeRecordDialog(false)}
                />
                <MenuItem onClick={() => setShowTimeRecordDialog(true)}>
                  Zeiterfassung
                </MenuItem>

                <Divider sx={{ m: 0, '& .MuiDivider-root': { m: 0 } }} />

                <MenuItem
                  onClick={() => {
                    setAnchorElUser(null);
                    navigate('/account');
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography textAlign='center'>Mein Konto</Typography>
                  <ListItemIcon>
                    <ManageAccounts fontSize='small' />
                  </ListItemIcon>
                </MenuItem>

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography textAlign='center'>Abmelden</Typography>
                  <ListItemIcon>
                    <Logout fontSize='small' />
                  </ListItemIcon>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant='permanent'
        open={isSidebarOpen}
        onMouseOver={() => setIsSidebarOpen(true)}
        onMouseLeave={() => setIsSidebarOpen(false)}
      >
        <DrawerHeader>
          <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <ListItemButton onClick={() => navigate('/')}>
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText>Startseite</ListItemText>
          </ListItemButton>
          {isCalendarEnabled && (
            <ListItemButton onClick={() => navigate('/calendar')}>
              <ListItemIcon>
                <CalendarMonth />
              </ListItemIcon>
              <ListItemText>Kalender</ListItemText>
            </ListItemButton>
          )}
          {isCalendarEnabled && (
            <ListItemButton onClick={() => navigate('/calendar/belegungsplan')}>
              <ListItemIcon>
                <EventSeat />
              </ListItemIcon>
              <ListItemText>Belegungsplan</ListItemText>
            </ListItemButton>
          )}
        </List>

        <Divider />
        <List>
          <ListItemButton onClick={() => navigate('/dialoginbox')}>
            <ListItemIcon>
              <MarkunreadMailbox />
            </ListItemIcon>
            <ListItemText>Importbereich</ListItemText>
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/order_book')}>
            <ListItemIcon>
              <BorderColor />
            </ListItemIcon>
            <ListItemText>Auftragsbuch</ListItemText>
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/legaltransaction')}>
            <ListItemIcon>
              <AccountBalance />
            </ListItemIcon>
            <ListItemText>Vorgänge</ListItemText>
          </ListItemButton>
          {!!scopes.find(scope => scope === 'admin') && (
            <ListItemButton onClick={() => navigate('/archive')}>
              <ListItemIcon>
                <Lock />
              </ListItemIcon>
              <ListItemText>Archiv</ListItemText>
            </ListItemButton>
          )}
          <ListItemButton onClick={() => navigate('/grundstuecke')}>
            <ListItemIcon>
              <MenuBook />
            </ListItemIcon>
            <ListItemText>Grundbuch</ListItemText>
          </ListItemButton>
        </List>

        <Divider />
        <List>
          {!!scopes.find(scope => scope === 'bill' || scope === 'admin') && (
            <>
              <ListItemButton
                onClick={() => navigate('/invoices/view/overview')}
              >
                <ListItemIcon>
                  <Receipt />
                </ListItemIcon>
                <ListItemText>Rechnungen</ListItemText>
              </ListItemButton>
            </>
          )}
          <ListItemButton onClick={() => navigate('/task')}>
            <ListItemIcon>
              <PlaylistAddCheck />
            </ListItemIcon>
            <ListItemText>Aufgaben</ListItemText>
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/clients')}>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText>Mandanten</ListItemText>
          </ListItemButton>

          <ListItemButton onClick={() => navigate('/document_portal')}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText>Posteingang</ListItemText>
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/tracking/trackings')}>
            <ListItemIcon>
              <AltRoute />
            </ListItemIcon>
            <ListItemText>Nachverfolgungen</ListItemText>
          </ListItemButton>
        </List>

        <Divider />
        <List>
          <ListItemButton onClick={() => navigate('/legaltransactiontypes')}>
            <ListItemIcon>
              <BackupTable />
            </ListItemIcon>
            <ListItemText>Rechtsgeschäftsarten</ListItemText>
          </ListItemButton>

          {/*
          <ListItemButton onClick={() => navigate('/aemter')}>
            <ListItemIcon>
              <Gavel />
            </ListItemIcon>
            <ListItemText>Ämter</ListItemText>
          </ListItemButton>
           */}

          <ListItemButton onClick={() => navigate('/textmodules')}>
            <ListItemIcon>
              <StickyNote2 />
            </ListItemIcon>
            <ListItemText>Vorlagen</ListItemText>
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/legalclientgroups')}>
            <ListItemIcon>
              <Groups />
            </ListItemIcon>
            <ListItemText>Mandantengruppen</ListItemText>
          </ListItemButton>
        </List>

        {/*!!scopes.find(scope => scope === 'reports' || scope === 'admin') && (
          <>
            <Divider />
            <List>
              <ListItemButton onClick={() => navigate('/reports')}>
                <ListItemIcon>
                  <InsertChart />
                </ListItemIcon>
                <ListItemText>Reports</ListItemText>
              </ListItemButton>
            </List>
          </>
        )*/}

        {!!scopes.find(
          scope => scope === 'timerecording' || scope === 'admin'
        ) &&
          configObject.Module.find(x => x === 'hr') !== undefined && (
            <>
              <Divider />
              <List>
                <ListItemButton onClick={() => navigate('/timerecording')}>
                  <ListItemIcon>
                    <AccessTime />
                  </ListItemIcon>
                  <ListItemText>Zeiterfassung</ListItemText>
                </ListItemButton>
                <ListItemButton onClick={() => navigate('/daysoffwork')}>
                  <ListItemIcon>
                    <CalendarMonth />
                  </ListItemIcon>
                  <ListItemText>Fehltage</ListItemText>
                </ListItemButton>
              </List>
              <Divider />
              {/*
              <List>
                <ListItemButton onClick={() => navigate('/users')}>
                  <ListItemIcon>
                    <ManageAccounts />
                  </ListItemIcon>
                  <ListItemText>Benutzer</ListItemText>
                </ListItemButton>
                <ListItemButton onClick={() => navigate('/userGroups')}>
                  <ListItemIcon>
                    <Group />
                  </ListItemIcon>
                  <ListItemText>Gruppen</ListItemText>
                </ListItemButton>
              </List>
               */}
            </>
          )}

        {/*<Divider />
        <List>
          <ListItemButton onClick={() => navigate('/tools')}>
            <ListItemIcon>
              <ConstructionIcon />
            </ListItemIcon>
            <ListItemText>Tools</ListItemText>
          </ListItemButton>
        </List>*/}

        {scopes?.find(x => x === 'systemmails' || x === 'admin') !==
          undefined && (
          <>
            <Divider />
            <List>
              <ListItemButton
                onClick={() => navigate('/system_mail/legal_clients')}
              >
                <ListItemIcon>
                  <Mail />
                </ListItemIcon>
                <ListItemText>Systemnachrichten</ListItemText>
              </ListItemButton>
              <ListItemButton onClick={() => navigate('/rating')}>
                <ListItemIcon>
                  <StarBorder />
                </ListItemIcon>
                <ListItemText>Bewertungsbögen</ListItemText>
              </ListItemButton>
            </List>
          </>
        )}

        {scopes?.find(x => x === 'admin') !== undefined && (
          <>
            <List>
              {/*
                <ListItemButton onClick={() => navigate('/admin/fsdirectory')}>
                  <ListItemIcon>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText>Ordnerverwaltung</ListItemText>
                </ListItemButton>


              <ListItemButton onClick={() => navigate('/admin/language')}>
                <ListItemIcon>
                  <Translate />
                </ListItemIcon>
                <ListItemText>Sprachpakete</ListItemText>
              </ListItemButton>

              <ListItemButton onClick={() => navigate('/admin/public')}>
                <ListItemIcon>
                  <Public />
                </ListItemIcon>
                <ListItemText>Öffentlicher Server</ListItemText>
              </ListItemButton>
               */}

              <ListItemButton onClick={() => navigate('/admin/globalmail')}>
                <ListItemIcon>
                  <MarkunreadMailbox />
                </ListItemIcon>
                <ListItemText>Globale E-Mails</ListItemText>
              </ListItemButton>
            </List>
          </>
        )}

        <Divider />
        <List>
          {scopes?.find(x => x === 'admin') !== undefined && (
            <ListItemButton onClick={() => navigate('/admin/user')}>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText>Einstellungen</ListItemText>
            </ListItemButton>
          )}
          <ListItemButton onClick={() => navigate('/systeminfo')}>
            <ListItemIcon>
              <Info />
            </ListItemIcon>
            <ListItemText>Systeminformationen</ListItemText>
          </ListItemButton>
        </List>
      </Drawer>

      <Box component='main' sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
};
